import { SearchResult } from "qpr-base/apihooks/useSearch";

export type ElementType = "text" | "image" | "html" | "table" | "";

export type DashboardMember = { id: string; name: string };

export type TextAlign = "start" | "end" | "left" | "center" | "right";

export type CachedFileUploadRequest = {
  hashFileName: string;
  fileContent: string;
};

export type TemplateRequest = {
  fileName: string;
};
export interface RenderedElement {
  type?: ElementType;
  /**
   * Base64 encoded image
   */
  png?: string;
  /**
   * Reference to the cached image inside the MongoDB for example
   * 2b0ea405d77c9f87092c9145c5d68a70f63263a5c91d9d030e721cc7874df7be
   */
  pngRef?: string;

  /**
   * String value for the rendering, for example for "text" fields
   */
  value?: string;

  /**
   * Size and position of the rendered element inside powerpoint
   */
  x?: number;
  y?: number;
  width?: number;
  height?: number;

  /**
   * This indicates where or not the Powerpoint should be scaling the font down so that the text can fit to the
   * given text box
   */
  autofit?: boolean;
}

/*
{
    "component": "Highcharts",
    "width": 924,
    "height": 440,
    "data": {
      "series": [
        {
          "name": "ACT",
          "title": "t(Actual)"
        },
        {
          "name": "TAR",
          "title": "t(Target)"
        }
      ],
      "elementId": "SC.291838041.1713670806",
      "period": "2014",
      "periodsBefore": 0,
      "periodsAfter": 20
    }
  }
*/

export type NewComponent = {
  action: "newComponent";
};

export type EditSlot = {
  action: "edit";
};

export type SearchDrag = {
  action: "search";
  data: SearchResult;
};

export type PastedComponentData = {
  tag: "COMPONENT";
  component: string; // TODO: list of available component types
  width: number;
  height: number;
  data: any;
  period?: string;
};

export type ElementPaste = {
  action: "paste";
  data: PastedComponentData;
};

export type ActiveEditAction =
  | NewComponent
  | SearchDrag
  | ElementPaste
  | EditSlot;

export type ColorRGB = {
  r: number;
  g: number;
  b: number;
};

/*
    public class  PortionInfo
    {
        public Boolean? isUnderline { get; set; } = false;
        public Boolean? isBold { get; set; } = false;
        public Boolean? isItalic { get; set; } = false;
        public float? fontSize { get; set; }
        public string? fontName { get; set; };
        public string text { get; set; } = "";

        public ColorRGB? fillColor { get; set; }
    }
*/

export type PortionInfo = {
  isUnderline?: boolean | null;
  isBold?: boolean | null;
  isItalic?: boolean | null;
  fontSize?: number | null;
  fontName?: string | null;
  text: string;
  fillColor?: ColorRGB | null;
  textAlign?: string;
};

/*
    public class ParagraphInfo
    {
        public float? fontSize { get; set; }
        public float? marginLeft { get; set; }
        public float? marginTop { get; set; } 
        public float? marginBottom { get; set; } 
        public float? marginRight { get; set; }
        public Boolean? rtl { get; set; } 

        public string? text { get; set; }
        public string? fontName { get; set; }

        public string? bulletChar { get; set; }
        public string? bulletType { get; set; }
        public ColorRGB? bulletColor { get; set; }
        public float? indent { get; set; }
        public float? bulletHeight { get; set; }

        public float? bulletNumberedStartFrom { get; set; } 

        public Boolean? isBulletHardColor { get; set; } 
        public PortionInfo[]? portions { get; set; }

    }
*/
export type ParagraphInfo = {
  fontSize?: number | null;
  marginLeft?: number | null;
  marginTop?: number | null;
  marginBottom?: number | null;
  marginRight?: number | null;
  rtl?: boolean | null;
  text?: string | null;
  textAlign?: string | null;
  fontName?: string | null;
  bulletChar?: string | null;
  bulletType?: string | null;
  indent?: number | null;
  bulletHeight?: number | null;
  bulletNumberedStartFrom?: number | null;
  isBulletHardColor?: boolean | null;
  portions?: PortionInfo[];
  bulletColor?: ColorRGB | null;
  languageId?: string;
};

export interface Element {
  id: string;
  /**
   * tag refers to the powerpoint Alt Text
   */
  tag: string;
  /**
   * type is the rendered type on the powerpoint
   */
  type: ElementType;

  /**
   * position of the element in the powerpoint slide
   */
  x: number;
  y: number;
  absolutex: number; // TODO: maybe deprecate these later
  absolutey: number; // TODO: maybe deprecate these later
  dx: number; // Parent group distance
  dy: number; // Parent group distance

  isStatic?: boolean;

  /**
   * Element width and height on the powerpoint / editor view,
   * these are scaled down in the editor
   */
  width: number;
  height: number;

  /**
   * base64 endocoded value which is sent to the Powerpoint renderer
   */
  png: string;
  svg?: string; // TODO: maybe deprecate these later

  /**
   * The component is used for rendering the component inside the Editor UI
   */
  component?: PastedComponentData | null;

  /**
   * This will contain the data which is used to generate the PDF or PowerPoing
   */
  rendered?: RenderedElement;

  /**
   * The rich text presentation of the element.
   */
  paragraphs?: ParagraphInfo[];
}

export interface Slide {
  id: string;
  title: string;
  slideNumber: number;
  width: number;
  height: number;
  tplWidth: number;
  tplHeight: number;
  tnPng: string;
  bgPng: string;
  bgPngRef?: string;
  renderedPng?: string;
  elements: Element[];
}

export type SlideMeta = {
  pptFile?: string;
  pages: Slide[];
};

export type TDragRuler = {
  type: "left" | "top" | "bottom" | "right" | "center";
  x?: number;
  y?: number;
  accuracy: number;
};

export type DragInfo = {
  startX?: number;
  startY?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
};

export enum EditorMode {
  preview,
  edit,
}

export type SlideMetaStorage = SlideMeta & {
  /**
   * Temporary page props
   */

  isLoading?: boolean;
  templateName?: string;

  /**
   * Editor properties
   */
  editorWidth: number;
  editorHeight: number;
  selectedPageId?: string;
  selectedElementId?: string;

  /**
   * All selected elements on the page, could be multiple elements
   */
  selectedElements: string[];

  isDragged: boolean;
  scaleFactor: number;
  dragInfo?: DragInfo;
  editedElement?: ActiveEditAction;
  mode: EditorMode;

  /**
   * Editor functions
   */

  hasUnsavedChanges: () => boolean;
  setUnsavedChanges: (value: boolean) => void;

  setEditorMode: (mode: EditorMode) => void;
  reorderPage: (id: string, newPos: number) => void;
  clearEditor: () => void;
  loadDashboard: (id: string, scaleToWidth: number) => void;
  loadSlideTemplate: (id: string, scaleToWidth: number) => Promise<void>;
  setEditAction: (data?: ActiveEditAction) => void;
  addPage: (newPage: Slide) => void;
  addElement: (element: Element) => void;
  removeActivePage: () => void;
  setDragInfo: (dragInfo: DragInfo) => void;
  setDragged: (isDragged: boolean) => void;
  initStore: (meta: SlideMeta, scale?: number) => void;
  setElementProperties: (
    pageId: string | undefined,
    elementId: string | undefined,
    values: Partial<Element>
  ) => void;
  setSelectedElementProperties: (values: Partial<Element>) => void;
  setSelectedPage: (pageId: string) => void;
  setPageProperties: (pageId: string, values: Partial<Slide>) => void;
  setSelectedElement: (elementId?: string, add?: boolean) => void;
  cloneActiveElement: () => void;
  removeSelectedElement: () => void;
  undo: () => void;
  redo: () => void;
  duplicateSlide: () => void;
  moveZOrder: (amount: number | "TOP" | "BOTTOM") => void;

  alignBorders: (
    borderToAlign:
      | "LEFT"
      | "TOP"
      | "BOTTOM"
      | "RIGHT"
      | "WIDTH"
      | "HEIGHT"
      | "HCENTER"
      | "VCENTER"
  ) => void;

  updateElementIdsForComponents: (
    pageId: string,
    oldElementId: string,
    newElementId: string
  ) => void;
  setFontSize: (value: number) => void;
  setColor: (value: ColorRGB) => void;
};
